/**
 * Key generation utilities for node identification in the graph.
 * This file contains the exact key generation logic moved from loadGraph.ts.
 */

export const KeyUtils = {
  /**
   * Creates a key for capability group nodes
   * From: createCapaGroupNode (line ~155)
   */
  forCapabilityGroup: (capabilityName: string, stageName: string): string => {
    return `${capabilityName}-${stageName}`;
  },

  /**
   * Creates a key for team nodes
   * From: createTeamNode (line ~184) and other instances in loadGraph
   */
  forTeamNode: (teamName: string, capabilityName: string, stageName: string): string => {
    return `${teamName}-${capabilityName}-${stageName}`;
  },

  /**
   * Creates a key for application group nodes
   * From: createAppGroupNode (line ~213)
   */
  forAppGroup: (applicationGroupName: string, teamName: string, capabilityName: string, stageName: string): string => {
    return `${applicationGroupName}-${teamName}-${capabilityName}-${stageName}`;
  },

  /**
   * Creates a key for application nodes
   * From: createAppNode (line ~254) and other usages in loadGraph
   */
  forAppNode: (appName: string, appGroupName?: string, teamName?: string, capabilityName?: string, stageName?: string): string => {
    if (appGroupName && teamName && capabilityName && stageName) {
      return `${appName}-${appGroupName}-${teamName}-${capabilityName}-${stageName}`;
    }
    // Fallback to just the app name if not all parameters are provided
    return appName;
  },

  /**
   * Creates a key for leaf nodes
   * Used to uniquely identify leaf nodes across different capabilities
   */
  forLeafNode: (leafName: string, appName: string, teamName: string, capabilityName: string, stageName: string): string => {
    return `${leafName}-${appName}-${teamName}-${capabilityName}-${stageName}`;
  },

  /**
   * Creates a key for edge source nodes
   * From: loadGraph (line ~490)
   */
  forEdgeSource: (leadAppName: string, leadAppGroupName: string, teamName: string, capabilityName: string, stageName: string): string => {
    return `${leadAppName}-${leadAppGroupName}-${teamName}-${capabilityName}-${stageName}`;
  },

  /**
   * Creates a key for edge target nodes
   * From: loadGraph (line ~491)
   */
  forEdgeTarget: (memberAppName: string, memberAppGroupName: string, teamName: string, capabilityName: string, stageName: string): string => {
    return `${memberAppName}-${memberAppGroupName}-${teamName}-${capabilityName}-${stageName}`;
  },
};

/**
 * Checks if two leaf objects (or tags) have the same unique identity.
 * Accepts either full LeafNode objects or tag objects with the relevant fields.
 */
export function isSameLeafNodeIdentity(
  a: { Leaf?: string; leafName?: string; Capability: string; Stage: string; Team: string; Application: string },
  b: { Leaf?: string; leafName?: string; Capability: string; Stage: string; Team: string; Application: string }
): boolean {
  // Accepts both 'Leaf' and 'leafName' for flexibility
  const aName = a.Leaf ?? a.leafName;
  const bName = b.Leaf ?? b.leafName;
  return (
    aName === bName &&
    a.Capability === b.Capability &&
    a.Stage === b.Stage &&
    a.Team === b.Team &&
    a.Application === b.Application
  );
}
